import {
    MediaGuideMetadata,
    MediaProgramMetadata,
    MediaProgramWithGuides,
} from '@/utils/media/types';
import {
    EnumProductAnalyticsEvent,
    EventProperties,
} from '@calm/evexia-analytics';
import { useIntersectionObserver } from '@rhg/hooks/use-intersection-observer';
import { usePackContext } from '@rhg/hooks/use-pack-context';
import { useTrackSessionEvent } from '@rhg/hooks/use-send-amplitude-event';
import { RefCallback, useCallback, useRef } from 'react';

interface UseTrackPackItemViewedEventProps {
    guide: MediaGuideMetadata | undefined;
    program: MediaProgramWithGuides | MediaProgramMetadata | undefined;
    trackProps?: EventProperties;
    disabled?: boolean;
}

/**
 * Hook that tracks the "Pack: Item: Viewed" analytics event when an element
 * becomes visible in the viewport. The event is only tracked once per component
 * instance to prevent duplicate tracking on scroll in/out. Attach the returned
 * ref to the element to watch.
 */
export function useTrackPackItemViewedEvent({
    guide,
    program,
    trackProps,
    disabled,
}: UseTrackPackItemViewedEventProps): RefCallback<Element> {
    const { trackPackItemEvent } = useTrackSessionEvent();
    const packTitle = usePackContext();
    const hasTrackedViewedRef = useRef(false);

    const handlePackItemViewed = useCallback(() => {
        if (hasTrackedViewedRef.current || !guide) {
            return;
        }
        trackPackItemEvent({
            eventName: EnumProductAnalyticsEvent.PackItemViewed,
            guide,
            program,
            packTitle,
            trackProps,
        });
        hasTrackedViewedRef.current = true;
    }, [trackPackItemEvent, guide, program, packTitle, trackProps]);

    return useIntersectionObserver({
        intersectingCallback: handlePackItemViewed,
        disabled,
    });
}
